import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import cn from "classnames";

import { passwordRestore, removeErrors } from "store/auth";
import Popup from "components/common/Popup";
import PopupTitle from "components/common/PopupTitle";
import FormikTextField from "components/InputsField/formikInputs/FormikTextField";
import Button from "components/common/Button";
import Error from "components/Forms/Error";
import { passwordValidation } from "./validate";

import styles from "./styles.module.scss";

const reServerError = /^5/;

const INITIAL_VALUES = {
    password: "",
    rePassword: "",
};

const NewPassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const authenticated = useSelector((state) => state.auth.authenticated);
    const error = useSelector((state) => state.auth.error);

    const [passVisible, setPassVisible] = useState(false);
    const [rePassVisible, setRePassVisible] = useState(false);

    const handlePassVisible = () => {
        setPassVisible(!passVisible);
    };
    const handleRePassVisible = () => {
        setRePassVisible(!rePassVisible);
    };

    const handleClose = useCallback(() => {
        navigate("/");
        dispatch(removeErrors());
    }, [navigate, dispatch]);

    const handleSubmit = useCallback(
        async (values) => {
            const dataToSend = {
                password: values.password,
                url: window.location.pathname.split("/")[2],
            };
            const response = await dispatch(passwordRestore(dataToSend));

            if (!response.payload.errors) {
                navigate("/profile/company");
            }
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        if (authenticated) {
            navigate("/profile/company");
        }
    }, [authenticated, navigate]);

    useEffect(() => {
        return () => {
            dispatch(removeErrors());
        };
    }, [dispatch]);

    return (
        <Popup onClose={handleClose}>
            <PopupTitle title={t("forgot_password.newPassword")} />
            <Formik
                initialValues={INITIAL_VALUES}
                validationSchema={passwordValidation(t)}
                onSubmit={handleSubmit}
            >
                <Form className={styles.form}>
                    <FormikTextField
                        name="password"
                        type={passVisible ? "text" : "password"}
                        maxLength="64"
                        label={t("registration.password")}
                        wrapperClassName={styles["field--with-margin"]}
                        autoComplete="new-password"
                        errorMode={"static"}
                        additionalComponent={
                            <button
                                type="button"
                                className={cn(styles.eye, passVisible && styles["eye--hide"])}
                                onClick={handlePassVisible}
                            />
                        }
                        required
                    />
                    <FormikTextField
                        name="rePassword"
                        type={rePassVisible ? "text" : "password"}
                        maxLength="64"
                        label={t("registration.repeat_password")}
                        wrapperClassName={styles["field--with-margin"]}
                        autoComplete="new-password"
                        additionalComponent={
                            <button
                                type="button"
                                className={cn(styles.eye, rePassVisible && styles["eye--hide"])}
                                onClick={handleRePassVisible}
                            />
                        }
                        required
                        errorMode={"static"}
                    />
                    <div className={styles.button}>
                        <Button type="submit">{t("login.login_button")}</Button>
                    </div>
                    {error && (
                        <div className={styles.errors}>
                            <Error>
                                {reServerError.test(error.status)
                                    ? t("login.serverError")
                                    : error.status.errors.map((item, index) => (
                                          <p>{t(`errors.${item.type}`)}</p>
                                      ))}
                            </Error>
                        </div>
                    )}
                </Form>
            </Formik>
        </Popup>
    );
};

export default NewPassword;
